import React from 'react';

import { ItemType as BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import axios, { AxiosError } from 'axios';

import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import { Menu } from 'models/Menu';
import { MenuNode } from 'models/MenuNode';

import DynamicIcon from '../components/DynamicIcon/DynamicIcon';
import { MAIN_BACKEND_URL } from '../config/Config';
import { MenuType } from '../config/Config';
import { DEFAULT_REDIRECT_AUTH_ROUTE } from '../config/Constants';
import i18n from '../config/i18n';

export interface BreadcrumbResult {
  title: string;
  breadcrumb: BreadcrumbItemType[];
  chain: MenuNode[];
}

export const FunctionsHelper = {
  getS3FileUrl: (s3RawKey: string) => {
    return `${MAIN_BACKEND_URL}/file/download/${encodeURIComponent(s3RawKey)}`;
  },
  // Obtiene el título a mostrar según el idioma actual, con fallback a `title`
  getLocalizedNodeTitle: (node: MenuNode): string => {
    const current = i18n.language === 'en' ? 'en' : 'es';
    const localized = node.localizedTitles?.[current] ?? null;
    return (localized ?? node.title) || node.title;
  },
  showS3FileUrl: (s3RawKey: string) => {
    return `${MAIN_BACKEND_URL}/file/show/${encodeURIComponent(s3RawKey)}`;
  },
  getErrorFormat: (error: any): ApiResponse<ApiErrorData> => {
    console.error(error);

    const status = 500;
    let errorCode = 'GENERIC_ERROR';
    let errorMessage = i18n.t('common.errors.network_error_connect_server');
    let payload = undefined;

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        const dataError = axiosError.response?.data
          ? axiosError.response?.data
            ? (axiosError.response?.data as any).data
            : undefined
          : undefined;

        if (dataError) {
          //Puede ser un string o un objeto con este formato: { code: string, message: string, payload?: number | string };
          if (typeof dataError === 'string') {
            errorCode = 'VALIDATION_OR_OTHER_ERROR';
            errorMessage = dataError;
          } else {
            // Si dataError es un objeto, extraemos sus propiedades para asignar el código, mensaje y carga útiles
            errorCode = dataError.code || 'UNKNOWN_CODE_ERROR';
            errorMessage =
              dataError.message || i18n.t('common.errors.server_generic_error');
            payload = dataError.payload || payload;
          }
        } else {
          errorCode = 'API_BAD_FORMAT_ERROR';
          errorMessage = i18n.t('common.errors.api_bad_format');
        }

        // Error de respuesta del servidor (4xx o 5xx)
        console.log(
          `Error: ${axiosError.response.status} - ${axiosError.response.statusText}`,
          dataError
        );
      } else if (axiosError.request) {
        // Error de solicitud (sin respuesta del servidor)
        errorCode = 'SERVER_NO_RESPONSE_ERROR';
        errorMessage = i18n.t('common.errors.request_no_response');
      } else {
        // Otros errores
        errorCode = 'APPLICATION_PROCESS_ERROR';
        errorMessage = i18n.t('common.errors.application_process_error');
      }
    }

    return {
      success: false,
      status,
      data: {
        code: errorCode,
        message: errorMessage,
        payload,
      },
    };
  },
  flattenMenuArray: (menuArray: Menu[]) => {
    let flattenedArray: any[] = [];
    menuArray.forEach((menu: any) => {
      flattenedArray.push(menu);
      if (menu.menus && menu.menus.length > 0) {
        flattenedArray = flattenedArray.concat(
          FunctionsHelper.flattenMenuArray(menu.menus)
        );
        // delete menu.menus;
      }
    });
    return flattenedArray;
  },
  flattenMenuArrayDashboardMenu: (menuArray: Menu[]) => {
    let flattenedArray: any[] = [];
    menuArray.forEach((menu: any) => {
      flattenedArray.push(menu);
      if (menu.children && menu.children.length > 0) {
        flattenedArray = flattenedArray.concat(
          FunctionsHelper.flattenMenuArrayDashboardMenu(menu.children)
        );
        // delete menu.children;
      }
    });
    return flattenedArray;
  },
  validateEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },
  sleep: async (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },
  normalizeText: (text: string | undefined) => {
    return (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  },
  capitalizeFirstLetter: (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  },
  getFilenameFromUrl: (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      return parts[parts.length - 1];
    } catch (error) {
      console.error('URL inválida:', error);
      return '';
    }
  },
  getDialCode: (countryCode: string): string => {
    let dialCode = '+56';

    switch (countryCode.toLowerCase()) {
      case 'pe':
        dialCode = '+51';
        break;
      case 'usa':
        dialCode = '+1';
        break;
      case 'mx':
        dialCode = '+52';
        break;
      case 'co':
        dialCode = '+57';
        break;
    }

    return dialCode;
  },
  generateUniqueId: () => {
    const timestamp = Date.now().toString(36); // Obtener el timestamp actual y convertirlo a base 36
    const randomNum = Math.random().toString(36).substring(2, 5); // Generar un número aleatorio y convertirlo a base 36
    return timestamp + randomNum; // Combinar el timestamp y el número aleatorio
  },
  /**
   * Encuentra recursivamente la cadena de nodos (padre→hoja) por `code` y tipos permitidos.
   * Si no se especifican tipos, acepta cualquier tipo distinto de `PARENT_MENU`.
   */
  findNodeChainByCodeAndTypes: (
    nodes: MenuNode[] = [],
    targetCode: string | undefined,
    allowedTypes?: Array<MenuType | string>,
    chain: MenuNode[] = []
  ): MenuNode[] | null => {
    if (!targetCode) return null;
    for (const node of nodes) {
      const nextChain = [...chain, node];
      const nodeType = String(node.menuTypeId);
      const isTypeAllowed =
        allowedTypes && allowedTypes.length > 0
          ? allowedTypes.map(String).includes(nodeType)
          : nodeType !== String(MenuType.PARENT_MENU);

      if (node.code === targetCode && isTypeAllowed) {
        return nextChain;
      }
      if (node.children && node.children.length > 0) {
        const found = FunctionsHelper.findNodeChainByCodeAndTypes(
          node.children,
          targetCode,
          allowedTypes,
          nextChain
        );
        if (found) return found;
      }
    }
    return null;
  },
  /**
   * Construye el breadcrumb a partir de una cadena de `MenuNode`.
   * Por defecto incluye el título del padre inmediato y el leaf con icono.
   */
  buildBreadcrumbFromChain: (
    chain: MenuNode[] = [],
    options?: {
      includeAllParents?: boolean;
      includeHome?: boolean;
      showLeafIcon?: boolean;
      parentsMode?: 'compact' | 'full';
    }
  ): BreadcrumbItemType[] => {
    const {
      includeAllParents = false,
      includeHome = false,
      showLeafIcon = true,
      parentsMode,
    } = options || {};

    const items: BreadcrumbItemType[] = [];
    if (!chain || chain.length === 0) return items;

    // Opcional: Home al inicio (solo título)
    if (includeHome) {
      items.push({ title: 'Home' });
    }

    // Padres
    const parents = chain.slice(0, chain.length - 1);
    const includeAll = parentsMode === 'full' || includeAllParents;
    if (includeAll) {
      parents.forEach((p) =>
        items.push({ title: FunctionsHelper.getLocalizedNodeTitle(p) })
      );
    } else if (parents.length > 0) {
      const parent = parents[parents.length - 1];
      items.push({ title: FunctionsHelper.getLocalizedNodeTitle(parent) });
    }

    // Leaf con icono opcional
    const leaf = chain[chain.length - 1];
    const leafTitle = FunctionsHelper.getLocalizedNodeTitle(leaf);
    const titleNode = React.createElement(
      'span',
      null,
      showLeafIcon && leaf.icon
        ? React.createElement(DynamicIcon, {
            type: (leaf.icon || '').trim(),
          })
        : null,
      ' ',
      leafTitle
    );
    items.push({ title: titleNode });

    return items;
  },
  /**
   * Genera título y breadcrumb a partir de `menus` y un `code`.
   */
  buildBreadcrumbForCode: (
    menus: MenuNode[] = [],
    code: string | undefined,
    options?: {
      allowedTypes?: Array<MenuType | string>;
      includeAllParents?: boolean;
      includeHome?: boolean;
      showLeafIcon?: boolean;
      parentsMode?: 'compact' | 'full';
      inheritPrevContainerSibling?: boolean;
      preferParentCodeAscend?: boolean;
    }
  ): BreadcrumbResult | null => {
    const chainByTree = FunctionsHelper.findNodeChainByCodeAndTypes(
      menus,
      code,
      options?.allowedTypes
    );
    let chain: MenuNode[] | null = chainByTree;
    if ((!chain || options?.preferParentCodeAscend) && code) {
      const codeMap = new Map<string, MenuNode>();
      const walk = (arr: MenuNode[]) => {
        arr.forEach((n) => {
          codeMap.set(n.code, n);
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(menus || []);
      const leaf = codeMap.get(code);
      if (leaf) {
        const asc: MenuNode[] = [];
        let current: MenuNode | undefined | null = leaf;
        while (current) {
          asc.unshift(current);
          const parentCode: string | null =
            (current as MenuNode).parentMenuCode || null;
          current = parentCode ? codeMap.get(parentCode) : null;
        }
        chain = asc.length > 0 ? asc : chainByTree;
      }
    }
    if (!chain) return null;
    let displayChain = chain;
    if (options?.inheritPrevContainerSibling && chain.length >= 2) {
      const parent = chain[chain.length - 2];
      const leaf = chain[chain.length - 1];
      const siblings = parent.children || [];
      const idx = siblings.findIndex((n) => n.code === leaf.code);
      if (idx > 0) {
        const prevContainer = [...siblings]
          .slice(0, idx)
          .reverse()
          .find(
            (n) =>
              String(n.menuTypeId) === String(MenuType.PARENT_MENU) ||
              (n.children && n.children.length > 0)
          );
        if (prevContainer) {
          displayChain = [
            ...chain.slice(0, chain.length - 1),
            prevContainer,
            leaf,
          ];
        }
      }
    }
    const leaf = displayChain[displayChain.length - 1];
    const leafTitle = FunctionsHelper.getLocalizedNodeTitle(leaf);
    const breadcrumb = FunctionsHelper.buildBreadcrumbFromChain(displayChain, {
      includeAllParents: options?.includeAllParents,
      includeHome: options?.includeHome,
      showLeafIcon: options?.showLeafIcon,
      parentsMode: options?.parentsMode,
    });
    return { title: leafTitle, breadcrumb, chain: displayChain };
  },
  /**
   * Genera título y breadcrumb a partir de `menus` y un `pathname` actual.
   * Coincide exactamente contra el path resuelto por el tipo del menú.
   */
  buildBreadcrumbForPath: (
    menus: MenuNode[] = [],
    pathname: string,
    options?: {
      includeAllParents?: boolean;
      includeHome?: boolean;
      showLeafIcon?: boolean;
      parentsMode?: 'compact' | 'full';
      inheritPrevContainerSibling?: boolean;
      preferParentCodeAscend?: boolean;
    }
  ): BreadcrumbResult | null => {
    if (!pathname) return null;

    // Caso especial Home
    if (pathname === DEFAULT_REDIRECT_AUTH_ROUTE) {
      const breadcrumb = (
        options?.includeHome ? [{ title: 'Home' }] : []
      ) as BreadcrumbItemType[];
      return { title: 'Home', breadcrumb, chain: [] };
    }

    const computePath = (node: MenuNode): string | null => {
      switch (node.menuTypeId) {
        case MenuType.IFRAME_VIEWER:
          return `/dynamic-viewer/${node.code}`;
        case MenuType.POWER_BI_REPORT:
          return `/pbi-viewer/${node.code}`;
        case MenuType.PDF_VIEWER:
          return `/pdf-viewer/${node.code}`;
        case MenuType.OFFICE_VIEWER:
          return `/office-viewer/${node.code}`;
        case MenuType.URL_REDIRECT:
          return `/download/${node.code}`;
        case MenuType.STANDARD_MENU:
          return node.redirectLink || DEFAULT_REDIRECT_AUTH_ROUTE;
        case MenuType.PARENT_MENU:
        default:
          return null;
      }
    };

    const findChainByPath = (
      nodes: MenuNode[] = [],
      targetPath: string,
      chain: MenuNode[] = []
    ): MenuNode[] | null => {
      for (const node of nodes) {
        const nextChain = [...chain, node];
        const path = computePath(node);
        if (path && path === targetPath) {
          return nextChain;
        }
        if (node.children && node.children.length > 0) {
          const found = findChainByPath(node.children, targetPath, nextChain);
          if (found) return found;
        }
      }
      return null;
    };

    const chainByPath = findChainByPath(menus, pathname, []);
    if (!chainByPath) return null;
    let chain = chainByPath;
    if (options?.preferParentCodeAscend) {
      const leafNode = chainByPath[chainByPath.length - 1];
      const codeMap = new Map<string, MenuNode>();
      const walk = (arr: MenuNode[]) => {
        arr.forEach((n) => {
          codeMap.set(n.code, n);
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(menus || []);
      const leaf = codeMap.get(leafNode.code);
      if (leaf) {
        const asc: MenuNode[] = [];
        let current: MenuNode | undefined | null = leaf;
        while (current) {
          asc.unshift(current);
          const parentCode: string | null =
            (current as MenuNode).parentMenuCode || null;
          current = parentCode ? codeMap.get(parentCode) : null;
        }
        chain = asc.length > 0 ? asc : chainByPath;
      }
    }
    let displayChain = chain;
    if (options?.inheritPrevContainerSibling && chain.length >= 2) {
      const parent = chain[chain.length - 2];
      const leaf = chain[chain.length - 1];
      const siblings = parent.children || [];
      const idx = siblings.findIndex((n) => n.code === leaf.code);
      if (idx > 0) {
        const prevContainer = [...siblings]
          .slice(0, idx)
          .reverse()
          .find(
            (n) =>
              String(n.menuTypeId) === String(MenuType.PARENT_MENU) ||
              (n.children && n.children.length > 0)
          );
        if (prevContainer) {
          displayChain = [
            ...chain.slice(0, chain.length - 1),
            prevContainer,
            leaf,
          ];
        }
      }
    }
    const leaf = displayChain[displayChain.length - 1];
    const leafTitle = FunctionsHelper.getLocalizedNodeTitle(leaf);
    const breadcrumb = FunctionsHelper.buildBreadcrumbFromChain(displayChain, {
      includeAllParents: options?.includeAllParents,
      includeHome: options?.includeHome,
      showLeafIcon: options?.showLeafIcon,
      parentsMode: options?.parentsMode,
    });
    return { title: leafTitle, breadcrumb, chain: displayChain };
  },
  getDateFormatByLang: (lng?: string): string => {
    const lang = (lng || i18n.language || 'es').toLowerCase();
    return lang.startsWith('en') ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  },
};
